import { Box, Flex, Group, Skeleton, Stack } from '@mantine/core';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { Icon, Text } from '@repo/foundations';
import { Handle, Position } from '@xyflow/react';
import { observer } from 'mobx-react-lite';
import EntityNodeChild from './EntityNodeChild';
import { ENTITY_CHILDREN_MAX_IN_VIEW } from './EntityNodeChildren.constants';
import { useEntityNodeChildren } from './EntityNodeChildren.hooks';
import { useStyles } from './EntityNodeChildren.styles';
import EntityChildrenSearch from './EntityNodeChildrenSearch';

interface EntityNodeChildrenProps {
	id: string;
	count: number;
}

const EntityNodeChildren = observer(
	({ id, count }: EntityNodeChildrenProps) => {
		const [searchTerm, setSearchTerm] = useInputState('');
		const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

		const {
			expanded,
			highlightedChildren,
			hasHighlightedChildren,
			filteredChildren,
			impactChildID,
			showSkeletons,
			showChildren,
			handleToggleExpand,
		} = useEntityNodeChildren(id, debouncedSearchTerm);

		const { classes, cx, theme } = useStyles({
			highlighted: hasHighlightedChildren,
		});

		return (
			<>
				<Stack className={classes.wrapper}>
					{count > 0 && (
						<Group className={classes.control} onClick={handleToggleExpand}>
							<Text
								size="xs"
								color="text/secondary/default"
							>{`${count} children`}</Text>
							<Icon
								name={expanded ? 'chevronUp' : 'chevronDown'}
								color="icon/primary/default"
							/>
						</Group>
					)}
					{expanded && (
						<>
							<EntityChildrenSearch term={searchTerm} setTerm={setSearchTerm} />
							<Stack className={classes.panel}>
								{showSkeletons &&
									[...Array(Math.min(ENTITY_CHILDREN_MAX_IN_VIEW, count))].map(
										(_, i) => (
											<Flex key={i} className={classes.skeleton}>
												<Skeleton height={theme.other.space[4]} />
											</Flex>
										)
									)}
								{showChildren && (
									<Stack
										className={cx(
											highlightedChildren
												? classes.highlightedPanel
												: classes.panel,
											'nowheel'
										)}
									>
										{filteredChildren?.map((child, i) => (
											<EntityNodeChild
												key={child.id}
												child={child}
												highlighted={hasHighlightedChildren}
												focused={impactChildID === child.id}
												order={i}
											/>
										))}
									</Stack>
								)}
							</Stack>
						</>
					)}
				</Stack>
				{!expanded &&
					filteredChildren?.map((child) => (
						<Box key={`EntityNodeChildren-HandleWrapper--${child.id}`}>
							<Handle
								className={classes.handle}
								id={`CHILD_TARGET--${child.id}`}
								type="target"
								position={Position.Left}
								isConnectable={false}
							/>
							<Handle
								className={classes.handle}
								id={`CHILD_SOURCE--${child.id}`}
								type="source"
								position={Position.Right}
								isConnectable={false}
							/>
						</Box>
					))}
			</>
		);
	}
);

export default EntityNodeChildren;
